import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import SpinTaskerLogo from '../components/shared/SpinTaskerLogo';
import { usePageTracking } from '../hooks/usePageTracking';
import { loadTutorialsManifest, isPublicTutorialUnlocked } from '../utils/tutorialsManifest';
import { trackTutorialEvent } from '../utils/tutorialAnalytics';
import { trackCtaClick, trackContactClick } from '../utils/googleAnalytics';
import { isAuthSessionValid } from '../utils/tokenUtils';
import { getAuthenticatedDashboardPath } from '../utils/appSession';
import { 
  ArrowRight, 
  CheckCircle, 
  Users, 
  Clock, 
  Shield, 
  BarChart3,
  Play,
  Menu,
  X,
  Zap,
  Globe,
  Smartphone,
  Monitor,
  Apple,
  Download,
  Calendar,
  Video,
  Wrench,
  Building2,
  Star,
  Award,
  TrendingUp,
  MessageSquare,
  FileText,
  Settings,
  Phone,
  Mail,
  Sun,
  Lock
} from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [isFieldServiceModalOpen, setIsFieldServiceModalOpen] = useState(false);
  const [isCrmModalOpen, setIsCrmModalOpen] = useState(false);
  const [qualityModal, setQualityModal] = useState(null);
  const [isInstallInstructionsOpen, setIsInstallInstructionsOpen] = useState(false);
  const [isTutorialsOpen, setIsTutorialsOpen] = useState(false);
  const [tutorials, setTutorials] = useState([]);
  const [tutorialsLoading, setTutorialsLoading] = useState(false);
  const [tutorialsError, setTutorialsError] = useState('');
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [tutorialVideoDebug, setTutorialVideoDebug] = useState({
    phase: 'idle',
    code: '',
    message: ''
  });
  const [tutorialVideoSrc, setTutorialVideoSrc] = useState('');
  const tutorialsSectionRef = useRef(null);

  useEffect(() => {
    if (activeTutorial?.videoUrl) {
      setTutorialVideoSrc(activeTutorial.videoUrl);
    } else {
      setTutorialVideoSrc('');
    }
  }, [activeTutorial]);

  // Ako je korisnik već ulogovan (validan token u localStorage), preusmeri na Poslove
  useEffect(() => {
    const dashboardPath = getAuthenticatedDashboardPath();
    if (dashboardPath) {
      navigate(dashboardPath, { replace: true });
    }
  }, [navigate]);

  // Track page visit
  usePageTracking('home');

  const handleGetStarted = (location = 'home', packageId) => {
    trackCtaClick('register', { cta_location: location });
    const params = new URLSearchParams({ role: 'company', type: 'register' });
    if (packageId) params.set('package', String(packageId));
    navigate(`/auth?${params.toString()}`);
  };

  const handleLogin = (location = 'home') => {
    trackCtaClick('login', { cta_location: location });
    navigate('/auth?role=company&type=login');
  };

  const handleTryDemo = (location = 'home') => {
    trackCtaClick('demo', { cta_location: location });
    navigate('/auth?role=company&type=login&demo=true');
  };

  const handleOpenTutorials = (location = 'home') => {
    trackCtaClick('tutorials', { cta_location: location });
    navigate('/tutorials');
  };

  const handleOpenInstallInstructions = (location = 'home') => {
    trackCtaClick('install_app', { cta_location: location });
    setIsInstallInstructionsOpen(true);
  };

  const handleMobileAction = (action) => {
    action();
    setMobileNavOpen(false);
  };

  useEffect(() => {
    if (!isTutorialsOpen) return;
    if (tutorials.length > 0 || tutorialsLoading) return;

    const loadTutorials = async () => {
      setTutorialsLoading(true);
      setTutorialsError('');
      try {
        const normalized = await loadTutorialsManifest();
        if (!Array.isArray(normalized) || normalized.length === 0) {
          throw new Error('Nema dostupnih tutorijala');
        }
        setTutorials(normalized);
      } catch (error) {
        setTutorialsError(
          'Ne mogu da učitam listu tutorijala. Proveri da li su tutorials manifest i video fajlovi dostupni na produkciji (R2/CDN ili /public/tuts).'
        );
      } finally {
        setTutorialsLoading(false);
      }
    };

    loadTutorials();
  }, [isTutorialsOpen, tutorials.length, tutorialsLoading]);

  const features = [
    {
      icon: <Clock className="h-8 w-8 text-green-500" />,
      title: t('home.features.realTimeTracking.title'),
      description: t('home.features.realTimeTracking.description')
    },
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: t('home.features.teamManagement.title'),
      description: t('home.features.teamManagement.description')
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-purple-500" />,
      title: t('home.features.analytics.title'),
      description: t('home.features.analytics.description')
    },
    {
      icon: <Shield className="h-8 w-8 text-red-500" />,
      title: t('home.features.security.title'),
      description: t('home.features.security.description')
    }
  ];

  // Package prices in EUR
  const packagePricesEUR = {
    starter: 19,
    starter: 19,
    standard: 39,
    business: 79,
    premium: 149
  };

  const pricingPlans = [
    {
      name: 'Starter',
      price: packagePricesEUR.starter,
      currency: 'EUR',
      period: ' /mesec',
      description: t('home.landing.pricing.starter.description'),
      features: [
        t('home.landing.pricing.starter.features.workers'),
        t('home.landing.pricing.starter.features.partners'),
        t('home.landing.pricing.starter.features.clients'),
        t('home.landing.pricing.starter.features.jobs'),
        t('home.landing.pricing.starter.features.crm'),
        t('home.landing.pricing.starter.features.dashboard'),
        t('home.landing.pricing.starter.features.archive'),
        t('home.landing.pricing.starter.features.analytics'),
        t('home.landing.pricing.starter.features.mobile')
      ],
      ctaKey: 'home.landing.pricing.starter.cta',
      popular: false,
      color: "bg-blue-50"
    },
    {
      name: 'Standard',
      price: packagePricesEUR.standard,
      currency: 'EUR',
      period: ' /mesec',
      description: t('home.landing.pricing.standard.description'),
      features: [
        t('home.landing.pricing.standard.features.workers'),
        t('home.landing.pricing.standard.features.partners'),
        t('home.landing.pricing.standard.features.marketing'),
        t('home.landing.pricing.standard.features.ai'),
        t('home.landing.pricing.standard.features.jobs'),
        t('home.landing.pricing.standard.features.inventory'),
        t('home.landing.pricing.standard.features.warehouseParts'),
        t('home.landing.pricing.standard.features.reversiWithdrawn'),
        t('home.landing.pricing.standard.features.workOrderPersonalization'),
        t('home.landing.pricing.standard.features.vehicles'),
        t('home.landing.pricing.standard.features.analytics')
      ],
      ctaKey: 'home.landing.pricing.standard.cta',
      popular: true,
      color: "bg-green-50"
    },
    {
      name: 'Business',
      price: packagePricesEUR.business,
      currency: 'EUR',
      period: ' /mesec',
      description: t('home.landing.pricing.business.description'),
      features: [
        t('home.landing.pricing.business.features.allFromStarter'),
        t('home.landing.pricing.business.features.allFromStandard'),
        t('home.landing.pricing.business.features.workers'),
        t('home.landing.pricing.business.features.partners'),
        t('home.landing.pricing.business.features.marketing'),
        t('home.landing.pricing.business.features.inventory'),
        t('home.landing.pricing.business.features.webshop'),
        t('home.landing.pricing.business.features.analytics'),
        t('home.landing.pricing.business.features.logoPersonalization')
      ],
      ctaKey: 'home.landing.pricing.business.cta',
      popular: false,
      color: "bg-purple-50"
    },
    {
      name: 'Premium',
      priceOnRequest: true,
      price: packagePricesEUR.premium,
      currency: 'EUR',
      period: ' /mesec',
      description: t('home.landing.pricing.premium.description'),
      features: [
        t('home.landing.pricing.premium.features.allFromBusiness'),
        t('home.landing.pricing.premium.features.ai'),
        t('home.landing.pricing.premium.features.support'),
        t('home.landing.pricing.premium.features.databases'),
        t('home.landing.pricing.premium.features.websites'),
        t('home.landing.pricing.premium.features.manager'),
        t('home.landing.pricing.premium.features.api')
      ],
      ctaKey: 'home.landing.pricing.premium.cta',
      popular: false,
      color: "bg-yellow-50"
    }
  ];

  const stats = [
    { number: "10,000+", label: t('home.stats.activeUsers') },
    { number: "99.9%", label: t('home.stats.uptime') },
    { number: "50+", label: t('home.stats.countries') },
    { number: "24/7", label: t('home.stats.support') }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-gray-900 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <SpinTaskerLogo />
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <LanguageSwitcher />
              <button
                onClick={() => handleLogin('nav')}
                className="text-gray-300 hover:text-green-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {t('auth.login')}
              </button>
              <button
                onClick={() => handleGetStarted('nav')}
                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
              >
                {t('auth.register')}
              </button>
              <button
                type="button"
                onClick={() => handleOpenInstallInstructions('nav')}
                className="flex items-center gap-1.5 text-gray-300 hover:text-green-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                aria-label="Kako instalirati aplikaciju"
              >
                <Download className="h-4 w-4" />
                <span>Preuzmi</span>
              </button>
            </div>
            <button
              type="button"
              className="md:hidden text-gray-300 hover:text-white focus:outline-none"
              onClick={() => setMobileNavOpen(prev => !prev)}
              aria-label="Toggle navigation menu"
            >
              {mobileNavOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {mobileNavOpen && (
            <div className="md:hidden absolute top-full left-0 w-full bg-gray-900 border-b border-gray-700 shadow-lg">
              <div className="px-4 py-4 space-y-3">
                <LanguageSwitcher />
                <button
                  onClick={() => handleMobileAction(() => handleLogin('nav_mobile'))}
                  className="block w-full text-left text-gray-300 hover:text-green-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {t('auth.login')}
                </button>
                <button
                  onClick={() => handleMobileAction(() => handleGetStarted('nav_mobile'))}
                  className="block w-full text-left bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  {t('auth.register')}
                </button>
                <button
                  type="button"
                  onClick={() => handleMobileAction(() => handleOpenInstallInstructions('nav_mobile'))}
                  className="flex w-full items-center gap-2 text-left text-gray-300 hover:text-green-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  aria-label="Kako instalirati aplikaciju"
                >
                  <Download className="h-4 w-4" />
                  <span>Preuzmi</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Summer promo strip — sunce (levo i desno) */}
      <section className="promo-banner-shimmer bg-gradient-to-r from-sky-100 via-sky-200/90 to-cyan-100 border-b border-sky-200">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-16 py-3 flex flex-col items-center text-center gap-3 md:flex-row md:items-center md:justify-between md:text-left text-slate-800">
          <div className="flex items-center justify-center gap-2 md:gap-3 w-full md:w-auto">
            <Sun className="shrink-0 h-5 w-5 md:h-6 md:w-6 text-amber-500" strokeWidth={2} aria-hidden="true" />
            <p className="text-sm md:text-base font-semibold leading-snug text-center md:text-left">
              Letnja promo ponuda za servise bele tehnike: Registrujte se do <strong>01.07.</strong> i koristi{' '}
              <span className="text-green-700">Spintasker</span> celo leto besplatno
            </p>
            <Sun className="shrink-0 h-5 w-5 md:h-6 md:w-6 text-amber-500" strokeWidth={2} aria-hidden="true" />
          </div>
          <button
            type="button"
            onClick={() => handleGetStarted('promo_summer')}
            className="shrink-0 w-full max-w-xs md:w-auto inline-flex items-center justify-center rounded-lg bg-sky-600 text-white px-4 py-2 text-sm font-bold hover:bg-sky-700 transition-colors"
          >
            Aktiviraj letnju akciju
            <ArrowRight className="ml-2 h-4 w-4" />
          </button>
        </div>
      </section>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text */}
            <div className="text-left">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                {t('home.landing.hero.brand')}<br />
                <span className="text-2xl md:text-3xl font-normal">
                  {t('home.landing.hero.tagline')}
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 mb-6">
                {t('home.landing.hero.lead1')}
              </p>
              <p className="text-lg text-gray-600 mb-8">
                {t('home.landing.hero.lead2')}
              </p>
              {isInstallInstructionsOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40 bg-black/40"
                      aria-hidden="true"
                      onClick={() => setIsInstallInstructionsOpen(false)}
                    />
                    <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-5 shadow-xl md:max-w-2xl">
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Kako instalirati SpinTasker
                        </h3>
                        <button
                          type="button"
                          onClick={() => setIsInstallInstructionsOpen(false)}
                          className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                          aria-label="Zatvori"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                      <p className="mb-4 text-sm text-gray-600">
                        Dodaj ikonicu na početni ekran ili računar da imaš brzi pristup.
                      </p>
                      <ul className="grid grid-cols-1 gap-4 text-sm text-gray-700 md:grid-cols-3">
                        <li className="flex flex-col gap-3 md:flex-col">
                          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                            <Monitor className="h-5 w-5" />
                          </span>
                          <div>
                            <span className="font-semibold text-gray-900">Desktop (Chrome / Edge)</span>
                            <p className="mt-0.5">U adresnoj traci klikni na ikonicu za instalaciju („Install app“ / „Instaliraj SpinTasker“) i prati korake.</p>
                          </div>
                        </li>
                        <li className="flex flex-col gap-3 md:flex-col">
                          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-700">
                            <Smartphone className="h-5 w-5" />
                          </span>
                          <div>
                            <span className="font-semibold text-gray-900">Android (Chrome)</span>
                            <p className="mt-0.5">Otvori SpinTasker, klikni na meni (⋮) gore desno i izaberi „Dodaj na početni ekran“ ili „Install app“.</p>
                          </div>
                        </li>
                        <li className="flex flex-col gap-3 md:flex-col">
                          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-200 text-gray-700">
                            <Apple className="h-5 w-5" />
                          </span>
                          <div>
                            <span className="font-semibold text-gray-900">iPhone (Safari)</span>
                            <p className="mt-0.5">Otvori SpinTasker, klikni na ikonicu za deljenje (kvadrat sa strelicom), pa izaberi „Add to Home Screen“.</p>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </>
                )}
            </div>
            
            {/* Right side - Image */}
            <div className="block">
              <img 
                src="https://img.freepik.com/premium-photo/repairman_762625-2862.jpg?w=2000" 
                alt={t('home.landing.hero.imageAlt')} 
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => handleOpenTutorials('hero')}
              className="w-full border-2 border-blue-300 text-blue-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center"
            >
              <Video className="mr-2 h-5 w-5" />
              {t('home.landing.hero.tutorials')}
            </button>
            <button
              onClick={() => handleGetStarted('hero')}
              className="w-full bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center shadow-lg"
            >
              {t('home.landing.hero.getStarted')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() => handleTryDemo('hero')}
              className="w-full border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center"
            >
              <Play className="mr-2 h-5 w-5" />
              {t('home.landing.hero.watchDemo')}
            </button>
          </div>
          
          {/* Features rows - full width below hero */}
          <div className="mt-12">
            {/* All items in one grid - 2 cols on mobile, 5 cols on desktop */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 items-start">
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.paperless')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium leading-tight text-sm md:text-base">
                  {t('home.landing.chips.smsViberMail')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.jobMonitoring')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.businessAnalytics')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.webshop')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.marketingNetworks')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.inventory')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.vehicles')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.loyalty')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.partners')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.aiAssistance')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.parkingReminder')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.fieldPrinting')}
                </span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.reversi')}
                </span>
              </div>
              <div className="hidden md:flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium lowercase leading-tight text-sm md:text-base">
                  {t('home.landing.chips.noExcel')}
                </span>
              </div>
            </div>
          </div>
          
          {/* Screenshot Gallery - Horizontal Layout */}
          <div className="mt-16 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 px-4">
            {/* First screenshot */}
            <div className="flex-1 max-w-md hover:scale-105 transition-all duration-300">
              <img 
                src="/screens/Screenshot1.png"
                alt="SpinTasker Screenshot 1" 
                className="rounded-2xl shadow-2xl w-full h-auto border-4 border-white"
                loading="lazy"
                onError={(e) => {
                  // Prevent infinite loop - hide image if it fails to load
                  e.target.style.display = 'none';
                  console.error('Error loading Screenshot1 - hiding image');
                }}
              />
            </div>
            
            {/* Second screenshot */}
            <div className="flex-1 max-w-md hover:scale-105 transition-all duration-300">
              <img 
                src="/screens/Screenshot2.png"
                alt="SpinTasker Screenshot 2" 
                className="rounded-2xl shadow-2xl w-full h-auto border-4 border-white"
                loading="lazy"
                onError={(e) => {
                  // Prevent infinite loop - hide image if it fails to load
                  e.target.style.display = 'none';
                  console.error('Error loading Screenshot2 - hiding image');
                }}
              />
            </div>
            
            {/* Third screenshot */}
            <div className="flex-1 max-w-md hover:scale-105 transition-all duration-300">
              <img 
                src="/screens/Screenshot3.png"
                alt="SpinTasker Screenshot 3" 
                className="rounded-2xl shadow-2xl w-full h-auto border-4 border-white"
                loading="lazy"
                onError={(e) => {
                  // Prevent infinite loop - hide image if it fails to load
                  e.target.style.display = 'none';
                  console.error('Error loading Screenshot3 - hiding image');
                }}
              />
            </div>
          </div>

          {/* Mobile Screenshot Gallery - Horizontal Layout */}
          <div className="mt-8 md:mt-12 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 px-4 overflow-x-auto">
            {/* Screenshot 4 */}
            <div className="flex-1 max-w-xs hover:scale-105 transition-all duration-300">
              <img 
                src="/screens/Screenshot4.png"
                alt="SpinTasker Mobile Screenshot 4" 
                className="rounded-2xl shadow-2xl w-full h-auto border-4 border-white"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Error loading Screenshot4 - hiding image');
                }}
              />
            </div>
            
            {/* Screenshot 5 */}
            <div className="flex-1 max-w-xs hover:scale-105 transition-all duration-300">
              <img 
                src="/screens/Screenshot5.png"
                alt="SpinTasker Mobile Screenshot 5" 
                className="rounded-2xl shadow-2xl w-full h-auto border-4 border-white"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Error loading Screenshot5 - hiding image');
                }}
              />
            </div>
            
            {/* Screenshot 6 */}
            <div className="flex-1 max-w-xs hover:scale-105 transition-all duration-300">
              <img 
                src="/screens/Screenshot6.png"
                alt="SpinTasker Mobile Screenshot 6" 
                className="rounded-2xl shadow-2xl w-full h-auto border-4 border-white"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Error loading Screenshot6 - hiding image');
                }}
              />
            </div>
            
            {/* Screenshot 7 */}
            <div className="flex-1 max-w-xs hover:scale-105 transition-all duration-300">
              <img 
                src="/screens/Screenshot7.png"
                alt="SpinTasker Mobile Screenshot 7" 
                className="rounded-2xl shadow-2xl w-full h-auto border-4 border-white"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Error loading Screenshot7 - hiding image');
                }}
              />
            </div>
            
            {/* Screenshot 8 */}
            <div className="flex-1 max-w-xs hover:scale-105 transition-all duration-300">
              <img 
                src="/screens/Screenshot8.png"
                alt="SpinTasker Mobile Screenshot 8" 
                className="rounded-2xl shadow-2xl w-full h-auto border-4 border-white"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Error loading Screenshot8 - hiding image');
                }}
              />
            </div>
            
            {/* Screenshot 9 */}
            <div className="flex-1 max-w-xs hover:scale-105 transition-all duration-300">
              <img 
                src="/screens/Screenshot9.png"
                alt="SpinTasker Mobile Screenshot 9" 
                className="rounded-2xl shadow-2xl w-full h-auto border-4 border-white"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Error loading Screenshot9 - hiding image');
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {isTutorialsOpen && (
        <section ref={tutorialsSectionRef} className="py-14 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-3 mb-7">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Video tutorijali</h2>
                <p className="text-gray-600 mt-2">
                  Praktični vodiči kroz SpinTasker funkcionalnosti.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsTutorialsOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white text-sm font-semibold"
              >
                Sakrij listu
              </button>
            </div>

            {tutorialsLoading && (
              <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-gray-600">
                Učitavanje tutorijala...
              </div>
            )}

            {tutorialsError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-red-700">
                {tutorialsError}
              </div>
            )}

            {!tutorialsLoading && !tutorialsError && tutorials.length === 0 && (
              <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-gray-600">
                Trenutno nema tutorijala. Dodajte unose u <code>/public/tuts/tutorials.json</code>.
              </div>
            )}

            {!tutorialsLoading && !tutorialsError && tutorials.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tutorials.map((tutorial, index) => {
                  const unlocked = isPublicTutorialUnlocked(index, isAuthSessionValid());
                  return (
                  <button
                    key={tutorial.id}
                    type="button"
                    onClick={() => {
                      if (!unlocked) {
                        handleLogin('home_tutorials_locked');
                        return;
                      }
                      setTutorialVideoDebug({ phase: 'opening', code: '', message: '' });
                      trackTutorialEvent({
                        event: 'play',
                        source: 'home_landing',
                        tutorialId: tutorial.id || `idx-${index}`,
                        title: tutorial.title || ''
                      });
                      setActiveTutorial(tutorial);
                    }}
                    className={`text-left rounded-xl border p-5 transition-all ${
                      unlocked
                        ? 'border-gray-200 bg-white hover:shadow-md hover:border-blue-300'
                        : 'border-amber-200 bg-amber-50/80 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {index + 1}. {tutorial.title}
                      </h3>
                      {tutorial.duration ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">
                          {tutorial.duration}
                        </span>
                      ) : null}
                    </div>
                    {tutorial.shortDescription ? (
                      <p className="mt-2 text-gray-700 text-sm leading-relaxed">
                        {tutorial.shortDescription}
                      </p>
                    ) : null}
                    {unlocked ? (
                      <div className="mt-4 inline-flex items-center text-blue-700 text-sm font-semibold">
                        <Play className="h-4 w-4 mr-1.5" />
                        Pusti tutorijal
                      </div>
                    ) : (
                      <div className="mt-4 inline-flex items-center text-amber-800 text-sm font-semibold">
                        <Lock className="h-4 w-4 mr-1.5" />
                        Dostupno nakon prijave
                      </div>
                    )}
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* What is it for Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('home.landing.why.title')}
            </h2>
            <div className="max-w-3xl mx-auto mb-12">
              <div className="bg-blue-50 rounded-xl p-8 mb-8">
                <p className="text-xl text-gray-700 mb-4">
                  <strong>{t('home.landing.why.bannerLine1')}</strong>
                </p>
                <p className="text-xl text-gray-700">
                  <strong>{t('home.landing.why.bannerLine2')}</strong>
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <Zap className="h-10 w-10 text-green-600 mr-4" />
                <h3 className="text-2xl font-bold text-gray-900">
                  {t('home.landing.why.cards.fastAssignment.title')}
                </h3>
              </div>
              <p className="text-gray-700 text-lg">
                {t('home.landing.why.cards.fastAssignment.description')}
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <Shield className="h-10 w-10 text-purple-600 mr-4" />
                <h3 className="text-2xl font-bold text-gray-900">
                  {t('home.landing.why.cards.noForgotten.title')}
                </h3>
              </div>
              <p className="text-gray-700 text-lg">
                {t('home.landing.why.cards.noForgotten.description')}
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <Users className="h-10 w-10 text-blue-600 mr-4" />
                <h3 className="text-2xl font-bold text-gray-900">
                  {t('home.landing.why.cards.oneSystem.title')}
                </h3>
              </div>
              <p className="text-gray-700 text-lg">
                {t('home.landing.why.cards.oneSystem.description')}
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <BarChart3 className="h-10 w-10 text-orange-600 mr-4" />
                <h3 className="text-2xl font-bold text-gray-900">
                  {t('home.landing.why.cards.calendar.title')}
                </h3>
              </div>
              <p className="text-gray-700 text-lg">
                {t('home.landing.why.cards.calendar.description')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-900 rounded-xl p-8 text-white">
              <Award className="h-12 w-12 text-yellow-400 mb-4" />
              <h3 className="text-2xl font-bold mb-4">
                {t('home.landing.why.cards.recognize.title')}
              </h3>
              <p className="text-gray-300 text-lg">
                {t('home.landing.why.cards.recognize.description')}
              </p>
            </div>

            <div className="bg-green-600 rounded-xl p-8 text-white">
              <TrendingUp className="h-12 w-12 text-white mb-4" />
              <h3 className="text-2xl font-bold mb-4">
                {t('home.landing.why.cards.investment.title')}
              </h3>
              <p className="text-green-100 text-lg">
                {t('home.landing.why.cards.investment.description')}
              </p>
            </div>
          </div>

          {/* Pricing Section - moved here */}
          <div className="mt-16">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {t('home.landing.pricing.title')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('home.landing.pricing.trialNote', 'Uz svaki paket ide mesec dana probnog perioda.')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {pricingPlans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative rounded-2xl p-6 ${plan.color} ${
                    plan.popular ? 'ring-2 ring-green-500 scale-105' : ''
                  } hover:shadow-xl transition-all flex flex-col`}
                  onMouseEnter={() => setHoveredPlan(index)}
                  onMouseLeave={() => setHoveredPlan(null)}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                      <span className="bg-green-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Najpopularniji
                      </span>
                    </div>
                  )}
                  
                  <div className="flex-1 flex flex-col text-center min-h-0">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="mb-4">
                      {plan.priceOnRequest ? (
                        <span className="text-2xl font-bold text-gray-900">{t('home.landing.pricing.premium.priceLabel')}</span>
                      ) : (
                        <>
                          <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                          <span className="text-lg font-semibold text-gray-700 ml-1">{plan.currency}</span>
                          <span className="text-gray-600 text-sm">{plan.period}</span>
                        </>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mb-4 min-h-[3rem]">{plan.description}</p>
                    
                    <ul className="space-y-2 mb-6 text-left flex-1">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() =>
                      handleGetStarted(
                        `pricing_${plan.name.toLowerCase()}`,
                        { Starter: 2, Standard: 3, Business: 4, Premium: 5 }[plan.name]
                      )
                    }
                    className={`w-full py-2 px-4 rounded-lg font-semibold transition-colors text-sm mt-auto shrink-0 ${
                      plan.popular
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {plan.ctaKey ? t(plan.ctaKey) : t('home.landing.pricing.starter.cta')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-10 sm:py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16 min-w-0 max-w-full mx-auto">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4 px-1 break-words">
              {t('home.landing.useCases.title')}
            </h2>
            <p className="text-base sm:text-xl text-gray-600 px-1 break-words">
              {t('home.landing.useCases.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 mb-6 sm:mb-8 min-w-0">
            {/* Terenski servis */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-xl active:shadow-md transition-shadow overflow-hidden flex flex-col min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-0 min-w-0 w-full">
                <div className="flex items-start gap-3 sm:mr-4 min-w-0 w-full">
                  <div className="rounded-xl bg-green-50 p-2.5 sm:p-3 flex-shrink-0">
                    <Wrench className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1 sm:mb-2 leading-tight break-words">
                      {t('home.landing.useCases.fieldService.title')}
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-3 sm:mb-4 break-words">
                      {t('home.landing.useCases.fieldService.systemProvides')}
                    </p>
                  </div>
                </div>
              </div>
              <ul className="text-gray-700 text-sm sm:text-base space-y-1.5 sm:space-y-2 mb-4 sm:mb-5 pl-0 sm:pl-0 min-w-0">
                <li className="flex items-start gap-2 min-w-0">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="min-w-0 break-words">{t('home.landing.useCases.fieldService.bullets.autoIntake')}</span>
                </li>
                <li className="flex items-start gap-2 min-w-0">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="min-w-0 break-words">{t('home.landing.useCases.fieldService.bullets.planning')}</span>
                </li>
                <li className="flex items-start gap-2 min-w-0">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="min-w-0 break-words">{t('home.landing.useCases.fieldService.bullets.analytics')}</span>
                </li>
              </ul>
              <button
                type="button"
                onClick={() => setIsFieldServiceModalOpen(true)}
                className="w-full sm:w-auto py-3 sm:py-2 px-4 rounded-xl sm:rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 active:bg-green-800 flex items-center justify-center gap-2 text-sm sm:text-base touch-manipulation"
              >
                {t('home.landing.useCases.fieldService.cta')}
                <ArrowRight className="h-4 w-4" />
              </button>
              <img 
                src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800&q=80" 
                alt={t('home.landing.useCases.fieldService.imageAlt')} 
                className="rounded-xl w-full h-36 sm:h-44 md:h-48 object-cover mt-4 sm:mt-5"
              />
            </div>

            {/* CRM */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-xl active:shadow-md transition-shadow overflow-hidden flex flex-col min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-0 min-w-0 w-full">
                <div className="flex items-start gap-3 sm:mr-4 min-w-0 w-full">
                  <div className="rounded-xl bg-purple-50 p-2.5 sm:p-3 flex-shrink-0">
                    <Settings className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1 sm:mb-2 leading-tight break-words">
                      {t('home.landing.useCases.crm.title')}
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-3 sm:mb-4 break-words">
                      {t('home.landing.useCases.crm.description')}
                    </p>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsCrmModalOpen(true)}
                className="w-full sm:w-auto py-3 sm:py-2 px-4 rounded-xl sm:rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 active:bg-purple-800 flex items-center justify-center gap-2 text-sm sm:text-base touch-manipulation mt-auto"
              >
                {t('home.landing.useCases.crm.cta')}
                <ArrowRight className="h-4 w-4" />
              </button>
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80" 
                alt={t('home.landing.useCases.crm.imageAlt')} 
                className="rounded-xl w-full h-36 sm:h-44 md:h-48 object-cover mt-4 sm:mt-5"
              />
            </div>
          </div>

        </div>
      </section>

      {/* Quality Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('home.landing.quality.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.landing.quality.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 text-center hover:shadow-xl transition-shadow">
              <Shield className="h-16 w-16 text-blue-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.landing.quality.cards.security.title')}</h3>
              <p className="text-gray-700 text-lg mb-6">
                {t('home.landing.quality.cards.security.description')}
              </p>
              <button
                type="button"
                onClick={() => setQualityModal('security')}
                className="text-blue-600 font-semibold hover:text-blue-700 flex items-center justify-center mx-auto"
              >
                {t('home.landing.quality.cards.security.cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-8 text-center hover:shadow-xl transition-shadow">
              <MessageSquare className="h-16 w-16 text-green-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.landing.quality.cards.support.title')}</h3>
              <p className="text-gray-700 text-lg mb-6">
                {t('home.landing.quality.cards.support.description')}
              </p>
              <button
                type="button"
                onClick={() => setQualityModal('support')}
                className="text-green-600 font-semibold hover:text-green-700 flex items-center justify-center mx-auto"
              >
                {t('home.landing.quality.cards.support.cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-8 text-center hover:shadow-xl transition-shadow">
              <Zap className="h-16 w-16 text-purple-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('home.landing.quality.cards.onboarding.title')}</h3>
              <p className="text-gray-700 text-lg mb-6">
                {t('home.landing.quality.cards.onboarding.description')}
              </p>
              <button
                type="button"
                onClick={() => setQualityModal('onboarding')}
                className="text-purple-600 font-semibold hover:text-purple-700 flex items-center justify-center mx-auto"
              >
                {t('home.landing.quality.cards.onboarding.cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('home.landing.testimonials.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('home.landing.testimonials.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mr-4">
                  JM
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900">{t('home.landing.testimonials.jelena.name')}</h4>
                </div>
              </div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 text-lg italic">
                "{t('home.landing.testimonials.jelena.quote')}"
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mr-4">
                  MĐ
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900">{t('home.landing.testimonials.marko.name')}</h4>
                </div>
              </div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
            ))}
              </div>
              <p className="text-gray-700 text-lg italic">
                "{t('home.landing.testimonials.marko.quote')}"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('home.landing.statsSection.title')}
            </h2>
            <p className="text-xl text-blue-100">
              {t('home.landing.statsSection.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center text-white">
              <div className="text-5xl font-bold mb-2">250+</div>
              <div className="text-blue-100 text-lg">{t('home.landing.statsSection.metrics.tasks')}</div>
            </div>
            <div className="text-center text-white">
              <div className="text-5xl font-bold mb-2">99.9%</div>
              <div className="text-blue-100 text-lg">{t('home.landing.statsSection.metrics.uptime')}</div>
            </div>
            <div className="text-center text-white">
              <div className="text-5xl font-bold mb-2">24/7</div>
              <div className="text-blue-100 text-lg">{t('home.landing.statsSection.metrics.support')}</div>
            </div>
            <div className="text-center text-white">
              <div className="text-5xl font-bold mb-2">100%</div>
              <div className="text-blue-100 text-lg">{t('home.landing.statsSection.metrics.security')}</div>
              </div>
          </div>
        </div>
      </section>



      {/* Field Service Modal */}
      {isFieldServiceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 relative">
            <button
              type="button"
              onClick={() => setIsFieldServiceModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close details"
            >
              <X className="h-6 w-6" />
            </button>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              {t('home.landing.useCases.fieldService.modal.title')}
            </h3>
            <p className="text-gray-700 text-lg mb-6">
              {t('home.landing.useCases.fieldService.modal.intro')}
            </p>
            <ul className="space-y-3 text-gray-800 text-base">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.fieldService.modal.benefits.copyFromMessages')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.fieldService.modal.benefits.smartScheduling')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.fieldService.modal.benefits.statusAndParking')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.fieldService.modal.benefits.liveOverview')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.fieldService.modal.benefits.historyAndAnalytics')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.fieldService.modal.benefits.onePlace')}</span>
              </li>
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
              <button
                type="button"
                onClick={() => setIsFieldServiceModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold"
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsFieldServiceModalOpen(false);
                  handleGetStarted('modal_field_service');
                }}
                className="px-5 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 text-sm font-semibold flex items-center justify-center"
              >
                {t('home.landing.hero.getStarted')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CRM Modal */}
      {isCrmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 relative">
            <button
              type="button"
              onClick={() => setIsCrmModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close details"
            >
              <X className="h-6 w-6" />
            </button>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              {t('home.landing.useCases.crm.modal.title')}
            </h3>
            <p className="text-gray-700 text-lg mb-6">
              {t('home.landing.useCases.crm.modal.intro')}
            </p>
            <ul className="space-y-3 text-gray-800 text-base">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.crm.modal.benefits.clientCard')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.crm.modal.benefits.intakeHandover')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.crm.modal.benefits.serviceOrders')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.crm.modal.benefits.technicianPayouts')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.crm.modal.benefits.clientCommunication')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.crm.modal.benefits.invoicingAndRevenue')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('home.landing.useCases.crm.modal.benefits.oneSystem')}</span>
              </li>
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
              <button
                type="button"
                onClick={() => setIsCrmModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold"
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsCrmModalOpen(false);
                  handleGetStarted('modal_crm');
                }}
                className="px-5 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm font-semibold flex items-center justify-center"
              >
                {t('home.landing.hero.getStarted')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {qualityModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setQualityModal(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="quality-modal-title"
          >
            <button
              type="button"
              onClick={() => setQualityModal(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label={t('common.close')}
            >
              <X className="h-6 w-6" />
            </button>
            <h3
              id="quality-modal-title"
              className={`text-2xl sm:text-3xl font-bold mb-3 pr-10 ${
                qualityModal === 'security'
                  ? 'text-blue-900'
                  : qualityModal === 'support'
                    ? 'text-green-900'
                    : 'text-purple-900'
              }`}
            >
              {t(`home.landing.quality.cards.${qualityModal}.title`)}
            </h3>
            <p className="text-gray-700 text-lg mb-6">
              {t(`home.landing.quality.cards.${qualityModal}.description`)}
            </p>
            <div
              className={`space-y-4 text-gray-800 text-base border-t pt-6 ${
                qualityModal === 'security'
                  ? 'border-blue-200'
                  : qualityModal === 'support'
                    ? 'border-green-200'
                    : 'border-purple-200'
              }`}
            >
              <p>{t(`home.landing.quality.cards.${qualityModal}.modal.p1`)}</p>
              <p>{t(`home.landing.quality.cards.${qualityModal}.modal.p2`)}</p>
              <p>{t(`home.landing.quality.cards.${qualityModal}.modal.p3`)}</p>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
              <button
                type="button"
                onClick={() => setQualityModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold"
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setQualityModal(null);
                  handleGetStarted(`modal_quality_${qualityModal}`);
                }}
                className={`px-5 py-2 rounded-lg text-white text-sm font-semibold flex items-center justify-center ${
                  qualityModal === 'security'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : qualityModal === 'support'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {t('home.landing.hero.getStarted')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTutorial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6 relative">
            <button
              type="button"
              onClick={() => {
                setActiveTutorial(null);
                setTutorialVideoSrc('');
              }}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-700"
              aria-label="Zatvori video tutorijal"
            >
              <X className="h-6 w-6" />
            </button>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 pr-8">
              {(() => {
                const i = tutorials.findIndex((t) => (t.id || '') === (activeTutorial.id || ''));
                const prefix = i >= 0 ? `${i + 1}. ` : '';
                return `${prefix}${activeTutorial.title}`;
              })()}
            </h3>
            {activeTutorial.shortDescription ? (
              <p className="text-sm sm:text-base text-gray-600 mt-2 mb-4">
                {activeTutorial.shortDescription}
              </p>
            ) : null}
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 break-all">
              <div><strong>Tutorial debug</strong></div>
              <div><strong>manifest videoUrl:</strong> {activeTutorial.videoUrl || '(prazno)'}</div>
              <div><strong>trenutni src:</strong> {tutorialVideoSrc || '(prazno)'}</div>
              {activeTutorial.fallbackVideoUrl ? (
                <div><strong>fallback (public):</strong> {activeTutorial.fallbackVideoUrl}</div>
              ) : null}
              <div><strong>Status:</strong> {tutorialVideoDebug.phase}</div>
              {tutorialVideoDebug.code ? <div><strong>Error code:</strong> {tutorialVideoDebug.code}</div> : null}
              {tutorialVideoDebug.message ? <div><strong>Message:</strong> {tutorialVideoDebug.message}</div> : null}
            </div>
            <video
              key={`${activeTutorial.id}-${tutorialVideoSrc}`}
              className="w-full rounded-xl bg-black"
              controls
              autoPlay
              preload="metadata"
              onLoadStart={() => {
                setTutorialVideoDebug((prev) => ({ ...prev, phase: 'loadstart', code: '', message: '' }));
                console.info('[tutorial-video] loadstart', { url: tutorialVideoSrc });
              }}
              onLoadedMetadata={() => {
                setTutorialVideoDebug((prev) => ({ ...prev, phase: 'metadata_loaded' }));
                console.info('[tutorial-video] metadata_loaded', { url: tutorialVideoSrc });
              }}
              onCanPlay={() => {
                setTutorialVideoDebug((prev) => ({ ...prev, phase: 'canplay' }));
                console.info('[tutorial-video] canplay', { url: tutorialVideoSrc });
              }}
              onPlaying={() => {
                setTutorialVideoDebug((prev) => ({ ...prev, phase: 'playing' }));
                console.info('[tutorial-video] playing', { url: tutorialVideoSrc });
              }}
              onError={(e) => {
                const mediaError = e?.currentTarget?.error;
                const code = mediaError?.code ? String(mediaError.code) : 'unknown';
                const message = mediaError?.message || 'Video failed to load/play';
                setTutorialVideoDebug({ phase: 'error', code, message });
                console.error('[tutorial-video] error', {
                  url: tutorialVideoSrc,
                  code,
                  message,
                  networkState: e?.currentTarget?.networkState,
                  readyState: e?.currentTarget?.readyState
                });
                if (
                  activeTutorial.fallbackVideoUrl &&
                  tutorialVideoSrc !== activeTutorial.fallbackVideoUrl
                ) {
                  setTutorialVideoSrc(activeTutorial.fallbackVideoUrl);
                  setTutorialVideoDebug({ phase: 'retry_fallback', code: '', message: '' });
                }
              }}
            >
              <source src={tutorialVideoSrc || activeTutorial.videoUrl} type="video/mp4" />
              Vaš browser ne podržava video element.
            </video>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
            <a
              href="tel:+381600301207"
              onClick={() => trackContactClick('phone')}
              className="inline-flex items-center gap-2 text-gray-700 hover:text-green-600 transition-colors"
              aria-label="Pozovi +381600301207"
            >
              <Phone className="h-5 w-5" />
              <span>+381 60 030 1207</span>
            </a>
            <a
              href="mailto:info@spintasker.com"
              onClick={() => trackContactClick('email')}
              className="inline-flex items-center gap-2 text-gray-700 hover:text-green-600 transition-colors"
              aria-label="Pošalji email info@spintasker.com"
            >
              <Mail className="h-5 w-5" />
              <span>info@spintasker.com</span>
            </a>
          </div>
          <div className="border-t border-gray-200 mt-6 pt-6 text-center">
            <p className="text-gray-600 text-sm">{t('home.footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;